// Chat cluster query helpers. Owns direct supabase.from('chat_messages'|'chat_participants')
// access so components stay off the raw client. Realtime channels are left in components.
import { supabase } from '@/integrations/supabase/client';
import type { ChatMessage } from '@/hooks/useTeamConversation';

export interface InsertChatMessageInput {
	conversationId: string;
	senderId: string;
	kind: string;
	body: string;
	attachments?: string[];
	metadata?: Record<string, unknown>;
}

export const insertChatMessage = async ({
	conversationId,
	senderId,
	kind,
	body,
	attachments = [],
	metadata,
}: InsertChatMessageInput) => {
	const payload: any = {
		conversation_id: conversationId,
		sender_id: senderId,
		kind,
		body,
		attachments,
	};
	if (metadata !== undefined) payload.metadata = metadata;
	return supabase.from('chat_messages').insert(payload);
};

/** Count messages in a conversation whose metadata jsonb contains the given filter. */
export const countMessagesWithMetadata = async (
	conversationId: string,
	metadataFilter: Record<string, unknown>,
): Promise<number> => {
	const { count } = await supabase
		.from('chat_messages')
		.select('id', { count: 'exact', head: true })
		.eq('conversation_id', conversationId)
		.contains('metadata', metadataFilter as any);
	return count ?? 0;
};

/** Latest chat_messages row in a conversation whose metadata jsonb contains the filter. */
export const fetchLatestMessageWithMetadata = async (
	conversationId: string,
	metadataFilter: Record<string, unknown>,
): Promise<{ id: string; kind: string; metadata: any; created_at: string } | null> => {
	const { data } = await supabase
		.from('chat_messages')
		.select('id, kind, metadata, created_at')
		.eq('conversation_id', conversationId)
		.contains('metadata', metadataFilter as any)
		.order('created_at', { ascending: false })
		.limit(1)
		.maybeSingle();
	return (data as any) ?? null;
};

export const updateChatParticipant = async (
	conversationId: string,
	userId: string,
	patch: { muted?: boolean; last_read_at?: string; metadata?: Record<string, unknown> },
) =>
	supabase
		.from('chat_participants')
		.update(patch as any)
		.eq('conversation_id', conversationId)
		.eq('user_id', userId);

export interface P2PThreadInit {
	messages: ChatMessage[];
	myParticipant: { muted: boolean; metadata: any } | null;
	otherLastReadAt: string | null;
}

export const fetchP2PThreadInit = async ({
	conversationId,
	userId,
	otherUserId,
}: {
	conversationId: string;
	userId: string | null | undefined;
	otherUserId: string | null | undefined;
}): Promise<P2PThreadInit> => {
	const [{ data: msgs }, myPartRes, otherPartRes] = await Promise.all([
		supabase
			.from('chat_messages')
			.select('*')
			.eq('conversation_id', conversationId)
			.order('created_at', { ascending: true }),
		userId
			? supabase
					.from('chat_participants')
					.select('muted, metadata')
					.eq('conversation_id', conversationId)
					.eq('user_id', userId)
					.maybeSingle()
			: Promise.resolve({ data: null as any }),
		otherUserId
			? supabase
					.from('chat_participants')
					.select('last_read_at')
					.eq('conversation_id', conversationId)
					.eq('user_id', otherUserId)
					.maybeSingle()
			: Promise.resolve({ data: null as any }),
	]);
	return {
		messages: (msgs ?? []) as ChatMessage[],
		myParticipant: (myPartRes as any)?.data
			? {
					muted: !!(myPartRes as any).data.muted,
					metadata: (myPartRes as any).data.metadata ?? null,
				}
			: null,
		otherLastReadAt: ((otherPartRes as any)?.data?.last_read_at as string) ?? null,
	};
};
